import { useMemo } from 'react';
import HandStuffedModel from './models/HandStuffedModel';
import PalletizedModel from './models/PalletizedModel';

// onBack and editPanel are optional and undefined by default — only Beyond
// CBM passes them (inline "Edit inputs" back button, and a live-editable
// panel that replaces the read-only hover summary). Console's call site
// (SimulationsTab.js) never passes either, so its header is unchanged.
// shareCta is optional too — Beyond CBM's share-link signup tease, rendered
// on the right side of the header.
// editPanelOpen/onEditPanelOpenChange make the edit panel's visibility a
// controlled prop instead of pure CSS :hover — Beyond CBM needs to be able
// to force it closed the moment "Run updated simulation" is clicked (a plain
// group-hover panel just sits there, covering the results as they update,
// since the mouse hasn't actually left). Console's read-only tooltip never
// passes these and keeps its original CSS-only hover behavior untouched.
// showPackageFilter/showLegend pass straight through to the 3D viewer — both
// default undefined, so PalletizedModel/HandStuffedModel's own `= false`
// defaults apply for console.
// sidePanel is optional too — the Examples page's always-visible inputs
// column (left of the header+3D card), as opposed to editPanel's hover
// popover. undefined by default, so Beyond CBM/console are unaffected; when
// present it wraps just the header+3D card in a row, leaving the Cost
// Analysis / Container Level Details tables full-width below as before.
// selectedStrategy is derived internally, not a prop — there's no more
// user-facing way to run multiple named strategies per simulation, so this
// always renders the (typically only) "default" strategy the backend
// returns. Older saved simulations with several strategies just show the
// first one, same as before this was ever selectable.
const SimulationResults = ({ results, inputContainers, inputPackages, cameraZoom, cameraRotationDeg, cameraTiltDeg, cameraDistanceOverride, cameraPanRight, edgeColor, canvasHeight, onBack, editPanel, editPanelOpen, onEditPanelOpenChange, shareCta, showPackageFilter, showLegend, sidePanel }) => {
  const selectedStrategy = Object.keys(results.strategies)[0];

  const costAnalysis = useMemo(() => {
    if (!results.strategies[selectedStrategy]) return [];
    const analysis = results.strategies[selectedStrategy].analysis;
    if (!analysis?.cost?.containers) return [];
    const rows = analysis.cost.containers.map((c) => ({
      container: c.container,
      quantity: c.quantity,
      totalCost: c.totalCost,
    }));
    rows.push({
      container: 'Total',
      quantity: analysis.cost.containerQuantity,
      totalCost: analysis.cost.totalCost,
    });
    return rows;
  }, [results, selectedStrategy]);

  const containerLevelDetails = useMemo(() => {
    if (!results.strategies[selectedStrategy]) return [];
    const getColor = (colorId) => {
      const key = Object.keys(results.entities.items).find((k) => k === colorId);
      return key ? results.entities.items[key].color : '';
    };
    return (results.strategies[selectedStrategy].packedContainers || []).map((c) => ({
      id: c.id,
      container: c.packingType,
      quantity: c.quantity,
      packagesCount: c.stats.itemCount,
      palletCount: c.stats.palletCount,
      cost: c.totalCost,
      totalWeight: c.stats.weight.value,
      weightUtilization: c.stats.weight.utilisation,
      totalVolume: c.stats.volume.value,
      volumeUtilization: c.stats.volume.utilisation,
      items: c.stats.itemDetails.map((item) => ({
        quantity: item.quantity,
        name: item.id,
        color: getColor(item.id),
      })),
    }));
  }, [results, selectedStrategy]);

  const packedContainers = results.strategies[selectedStrategy]?.packedContainers || [];

  return (
    <section className="space-y-6">
      <div className={sidePanel ? "flex flex-col gap-6 lg:flex-row lg:items-start" : undefined}>
      {sidePanel && <div className="lg:w-80 lg:shrink-0">{sidePanel}</div>}
      <div className="min-w-0 flex-1 rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                aria-label="Edit inputs"
                title="Edit inputs"
                className="flex items-center text-slate-400 hover:text-white transition"
              >
                <span className="material-symbols-outlined text-xl">arrow_back</span>
              </button>
            )}
            <h2 className="text-2xl font-black tracking-tight text-white">Simulation Results</h2>
            {(inputContainers?.length > 0 || inputPackages?.length > 0) && (
              <div
                className="group relative flex"
                {...(editPanel ? {
                  onMouseEnter: () => onEditPanelOpenChange?.(true),
                  onMouseLeave: () => onEditPanelOpenChange?.(false),
                  onFocus: () => onEditPanelOpenChange?.(true),
                  onBlur: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) onEditPanelOpenChange?.(false); },
                } : {})}
              >
                {editPanel ? (
                  <span className="flex cursor-pointer items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs font-semibold text-slate-400 transition hover:border-primary/60 hover:text-white">
                    <span className="material-symbols-outlined text-sm">edit</span>
                    Edit &amp; rerun
                  </span>
                ) : (
                  <span className="material-symbols-outlined cursor-pointer text-lg text-slate-500 hover:text-primary transition">input</span>
                )}
                <div
                  className={
                    editPanel
                      ? `absolute left-0 top-full z-20 max-w-[90vw] w-[44rem] rounded-xl border border-white/10 bg-[#0d1421] p-4 text-xs text-slate-300 shadow-xl transition pointer-events-none ${editPanelOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0'}`
                      : 'absolute left-0 top-full z-20 max-w-[90vw] w-80 rounded-xl border border-white/10 bg-[#0d1421] p-4 text-xs text-slate-300 opacity-0 shadow-xl transition pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto'
                  }
                >
                  {editPanel ? editPanel : (
                    <>
                      <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">Containers</p>
                      <div className="mb-3 space-y-1.5">
                        {(inputContainers || []).map((c, i) => (
                          <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5">
                            <span className="font-semibold text-white">{c.name}</span>
                            <span className="whitespace-nowrap text-slate-400">{c.length}×{c.width}×{c.height}m</span>
                          </div>
                        ))}
                      </div>
                      <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">Items</p>
                      <div className="space-y-1.5">
                        {(inputPackages || []).map((p, i) => (
                          <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5">
                            <span className="font-semibold text-white">{p.name} <span className="font-normal text-slate-500">×{p.quantity}</span></span>
                            <span className="whitespace-nowrap text-slate-400">{p.length}×{p.width}×{p.height}m</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
          {shareCta}
        </div>

        {/* 3D Visualization */}
        <div className="mt-6 rounded-xl overflow-hidden border border-white/10">
          {results.entities.handlingType === 'palletized' ? (
            <PalletizedModel entities={results.entities} packedContainers={packedContainers} cameraZoom={cameraZoom} cameraRotationDeg={cameraRotationDeg} cameraTiltDeg={cameraTiltDeg} cameraDistanceOverride={cameraDistanceOverride} cameraPanRight={cameraPanRight} edgeColor={edgeColor} canvasHeight={canvasHeight} showPackageFilter={showPackageFilter} showLegend={showLegend} />
          ) : (
            <HandStuffedModel entities={results.entities} packedContainers={packedContainers} cameraZoom={cameraZoom} cameraRotationDeg={cameraRotationDeg} cameraTiltDeg={cameraTiltDeg} cameraDistanceOverride={cameraDistanceOverride} cameraPanRight={cameraPanRight} edgeColor={edgeColor} canvasHeight={canvasHeight} showPackageFilter={showPackageFilter} showLegend={showLegend} />
          )}
        </div>
      </div>
      </div>

      {packedContainers.length > 0 && (
        <>
          {/* Cost Analysis */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8">
            <h3 className="text-lg font-bold text-white">Cost Analysis</h3>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left">
                    <th className="pb-3 pr-6 text-xs font-bold uppercase tracking-widest text-slate-400">Container Type</th>
                    <th className="pb-3 pr-6 text-xs font-bold uppercase tracking-widest text-slate-400">Quantity</th>
                    <th className="pb-3 text-xs font-bold uppercase tracking-widest text-slate-400">Cost (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {costAnalysis.map((row, idx) => (
                    <tr key={idx} className={`border-t border-white/5 ${row.container === 'Total' ? 'font-bold text-white' : 'text-slate-300'}`}>
                      <td className="py-3 pr-6">{row.container}</td>
                      <td className="py-3 pr-6">{row.quantity}</td>
                      <td className="py-3">${row.totalCost?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Container Level Details */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8">
            <h3 className="text-lg font-bold text-white">Container Level Details</h3>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left">
                    {['Name', 'Container Type', 'Qty', 'Packages', 'Items', 'Cost', 'Weight (kg)', 'Wt %', 'Volume (m³)', 'Vol %'].map((h) => (
                      <th key={h} className="pb-3 pr-4 text-xs font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {containerLevelDetails.map((row, idx) => (
                    <tr key={`${row.id}-${idx}`} className="border-t border-white/5 text-slate-300">
                      <td className="py-3 pr-4 font-medium text-white">{row.id}</td>
                      <td className="py-3 pr-4 whitespace-nowrap">{row.container}</td>
                      <td className="py-3 pr-4">{row.quantity}</td>
                      <td className="py-3 pr-4">{row.packagesCount}</td>
                      <td className="py-3 pr-4">
                        {row.items.map((item) => (
                          <div key={item.name} className="flex items-center gap-2 py-0.5">
                            <span className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: item.color }} />
                            <span className="whitespace-nowrap">{item.name} (×{item.quantity})</span>
                          </div>
                        ))}
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">${row.cost?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td className="py-3 pr-4">{row.totalWeight}</td>
                      <td className="py-3 pr-4">{row.weightUtilization}%</td>
                      <td className="py-3 pr-4">{row.totalVolume}</td>
                      <td className="py-3">{row.volumeUtilization}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {packedContainers.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-slate-400">
          No simulation data available.
        </div>
      )}
    </section>
  );
};

export default SimulationResults;
